import { useId } from 'react'
import { Network, PanelLeftOpen, PanelsTopLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'

export type WorkspaceViewMode = 'canvas' | 'simplified'
const VIEW_MODES: readonly WorkspaceViewMode[] = ['canvas', 'simplified']

export interface WorkspaceViewSwitchProps {
  value: WorkspaceViewMode
  onChange: (mode: WorkspaceViewMode) => void
  /**
   * `palette` renders the borderless segmented pair embedded in
   * <CanvasBottomPalette> (Figma "Canvas UI 2.0" Navbar, node 355:6242), with
   * that frame's Network / PanelLeftOpen glyphs. `standalone` is the bordered
   * floating card the list view still uses.
   */
  variant?: 'standalone' | 'palette'
}

/** Curved connector with two endpoints, matching the supplied canvas glyph. */
function CanvasViewIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx={6.5} cy={6.5} r={3.5} />
      <path d="M15 5h3c6 0 8 4 3 8l-10 7c-6 4-4 7 1 7h4" />
      <circle cx={25.5} cy={25.5} r={3.5} />
    </svg>
  )
}

function ModeIcon({ mode, variant }: { mode: WorkspaceViewMode; variant: 'standalone' | 'palette' }) {
  if (variant === 'palette') {
    const Icon = mode === 'canvas' ? Network : PanelLeftOpen
    return <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
  }
  return mode === 'canvas' ? <CanvasViewIcon /> : <PanelsTopLeft className="h-4 w-4" aria-hidden="true" />
}

export function WorkspaceViewSwitch({ value, onChange, variant = 'standalone' }: WorkspaceViewSwitchProps) {
  const { t } = useTranslation('workspace')
  const tooltipId = useId()
  const isPalette = variant === 'palette'
  return (
    <div
      role="group"
      aria-label={t('viewMode.label', 'Workspace view')}
      data-testid="workspace-view-switch"
      className={cn(
        'flex shrink-0 items-center',
        isPalette
          ? 'rounded-lg bg-white/20'
          : 'gap-1 rounded-xl border border-input-border bg-surface-1 p-1 shadow-lg',
      )}
    >
      {VIEW_MODES.map((mode) => {
        const label = t(`viewMode.${mode}`, mode === 'canvas' ? 'canvas view' : 'list view')
        const selected = value === mode
        return (
          <button
            key={mode}
            type="button"
            aria-label={label}
            aria-describedby={`${tooltipId}-${mode}`}
            aria-pressed={selected}
            data-testid={`workspace-view-${mode}`}
            onClick={() => onChange(mode)}
            className={cn(
              'group relative flex items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              isPalette
                ? cn(
                    'h-[31px] w-[31px]',
                    selected ? 'bg-[#3D4143] text-chrome' : 'text-chrome/75 hover:bg-white/10 hover:text-chrome',
                  )
                : cn(
                    'h-7 w-9 border',
                    selected
                      ? 'border-input-border-hover bg-surface-3 text-foreground'
                      : 'border-transparent text-muted-foreground hover:border-input-border-hover hover:bg-surface-2 hover:text-foreground',
                  ),
            )}
          >
            <ModeIcon mode={mode} variant={variant} />
            <span id={`${tooltipId}-${mode}`} role="tooltip" className="pointer-events-none invisible absolute bottom-full right-0 z-50 mb-2 whitespace-nowrap rounded-lg border border-border bg-background px-2 py-1 text-xs font-medium text-foreground shadow-lg group-hover:visible group-focus-visible:visible">
              {label}
            </span>
          </button>
        )
      })}
    </div>
  )
}
